/**
 * packages/shared/src/scoring/datadog-namespace-coverage.ts
 *
 * SIL-151 — connect-time (and settings-page) Datadog coverage report.
 *
 * Turns a connection's discovery record (datadog_integration.detected_namespaces
 * + namespaces_discovered_at, populated lazily by the SIL-148 worker's
 * `maybeDiscoverNamespaces` / `discoverNamespaces`) into an honest,
 * customer-facing summary: which namespaces were found, which v5 GPU_Health
 * fields their telemetry actually covers, a static confidence-expectation
 * blurb, and whether to recommend enabling dcgm-exporter's opt-in ECC
 * counters.
 *
 * PROVISIONAL, like the adapter it mirrors: the field-coverage lists below
 * are derived from apps/telemetry-ingest/src/adapters/datadog.ts's
 * DCGM_METRIC_MAP / DCGM_EXTRA_METRIC_MAP / DCGM_ECC_METRIC_MAP (SIL-148,
 * itself sourced from resources/Datadog_app_plan.md decisions 8/9 — not yet
 * verified against a real capture). SIL-144 (real-exporter ground-truth
 * capture) is the follow-up that locks these names for real. The `gpu.`
 * (native GPU Monitoring) coverage list is a conservative estimate — the
 * adapter does not implement a `gpu.*` metric mapping yet, so it only claims
 * the baseline vitals plan decision 8 describes ("gpu.* secondary — fewer
 * wear signals"), never the dcgm-exporter-only wear counters.
 *
 * NOTE: this module intentionally has NO dependency on the v5 scoring
 * engine or telemetry-ingest — it does not compute or influence health
 * scores (SIL-150 owns scoring). It only classifies discovery output into a
 * display-ready shape.
 */

/** Structural mirror of telemetry-ingest's NamespaceDiscovery (SIL-148) —
 * kept local so @siliconx/shared has no dependency on apps/telemetry-ingest. */
export interface DatadogNamespaceDiscoveryLike {
  prefix: string;
  metric_count: number;
  supported: boolean;
}

export type DatadogNamespacePrefix = 'dcgm.' | 'gpu.' | 'nvml.';

export const DATADOG_DISCOVERY_PREFIXES: readonly DatadogNamespacePrefix[] = [
  'dcgm.',
  'gpu.',
  'nvml.',
];

/** One covered v5 field, with a short customer-facing label. */
export interface CoveredField {
  telemetryName: string;
  label: string;
}

/**
 * v5 fields covered by `gpu.*` (native GPU Monitoring, Agent 7.74+).
 *
 * This is now the PRIMARY namespace. Verified against a real org in SIL-446:
 * 42 metrics vs dcgm.*'s 21, including ECC and PCIe degradation, which the
 * dcgm.* path cannot deliver at all. Mirrors GPU_METRIC_MAP +
 * GPU_EXTRA_METRIC_MAP in apps/telemetry-ingest/src/adapters/datadog.ts.
 */
export const DATADOG_GPU_COVERED_FIELDS: readonly CoveredField[] = [
  { telemetryName: 'GPU_Utilization_Percent', label: 'GPU utilization' },
  { telemetryName: 'GPU_Temperature_Current_C', label: 'GPU core temperature' },
  { telemetryName: 'GPU_Power_Draw_Current_W', label: 'Power draw' },
  { telemetryName: 'GPU_Power_Limit_W', label: 'Power limit' },
  { telemetryName: 'GPU_SM_Clock_MHz', label: 'SM clock' },
  { telemetryName: 'GPU_Memory_Clock_MHz', label: 'Memory clock' },
  { telemetryName: 'GPU_Memory_Used_MB', label: 'Memory used' },
  { telemetryName: 'GPU_Memory_Free_MB', label: 'Memory free' },
  { telemetryName: 'GPU_Memory_Utilization_Percent', label: 'Memory utilization' },
  { telemetryName: 'GPU_Performance_State', label: 'Performance (P-state)' },
  { telemetryName: 'GPU_Count_Detected', label: 'GPU count' },
  { telemetryName: 'ECC_Correctable_Aggregate', label: 'ECC correctable errors' },
  { telemetryName: 'ECC_Uncorrectable_Aggregate', label: 'ECC uncorrectable errors' },
  { telemetryName: 'Row_Remap_Count', label: 'Memory repair pending' },
  { telemetryName: 'PCIe_Replay_Counter', label: 'PCIe replay count' },
  { telemetryName: 'PCIe_Link_Width_Current', label: 'PCIe link width' },
  { telemetryName: 'PCIe_Link_Downgrade_Flag', label: 'PCIe link degraded' },
  { telemetryName: 'GPU_Thermal_Throttle_Active', label: 'Thermal throttle (active)' },
  { telemetryName: 'GPU_Thermal_Throttle_Count', label: 'Thermal throttle events' },
  { telemetryName: 'GPU_Power_Throttle_Active', label: 'Power throttle (active)' },
  { telemetryName: 'GPU_Power_Throttle_Count', label: 'Power throttle events' },
];

/**
 * SIL-479 — v5 fields ONLY reachable via `dcgm.*`. `gpu.*` (Native GPU
 * Monitoring) has no XID metric and no hardware-slowdown/power-brake
 * throttle reason at all — these are a genuinely `dcgm.*`-exclusive signal,
 * not a duplicate of anything gpu.* already claims.
 *
 * Kept as its own list (rather than folded silently into
 * DATADOG_DCGM_COVERED_FIELDS) so `deriveDatadogCoverage` can credit it to
 * an org that has `dcgm.*` regardless of whether `gpu.*` is ALSO present —
 * the AC4 requirement: XID must be credited to dcgm.* specifically, not
 * silently dropped when gpu.* wins as primary namespace.
 */
export const DATADOG_DCGM_XID_FIELDS: readonly CoveredField[] = [
  { telemetryName: 'GPU_Last_XID_Code', label: 'Last hardware-fault code (XID)' },
  { telemetryName: 'GPU_XID_Event_Count', label: 'Hardware-fault events (XID)' },
  { telemetryName: 'GPU_Critical_XID_Count', label: 'Critical hardware faults' },
  { telemetryName: 'GPU_Fallen_Off_Bus_Count', label: 'GPU fallen off bus events' },
];

/**
 * The throttle-bitmask fields — SPLIT OUT of DATADOG_DCGM_XID_FIELDS, because
 * crediting them to every `dcgm.*` org was an over-claim.
 *
 * All four decode from a single metric, `dcgm.clock_throttle_reasons`
 * (DCGM_FI_DEV_CLOCK_THROTTLE_REASONS). That counter **ships commented out**:
 * measured 2026-08-06 on a live Tesla T4 running dcgm-exporter 4.2.3, the
 * shipped `default-counters.csv` is 90 lines with 51 commented, and the
 * throttle bitmask is one of them. A stock exporter emits 21 metric families
 * and this is not among them — confirmed against the running org, not just
 * the config file.
 *
 * SIL-479 saw the metric populated only because
 * `scripts/aws/provision-dcgm-testbox.sh` explicitly adds an ECC + throttle
 * block to the counters CSV. That is our provisioning, not the default a
 * customer runs.
 *
 * So these are reachable but not automatic — same shape as the ECC claim
 * SIL-469 had to correct, and reported the same way: as a recommendation with
 * the config to close it, never as coverage the org already has.
 *
 * Unlike ECC, enabling this one WORKS end to end: the Datadog DCGM check does
 * forward the throttle bitmask (SIL-479 verified it live), where it drops ECC
 * regardless of exporter config.
 */
export const DATADOG_DCGM_THROTTLE_FIELDS: readonly CoveredField[] = [
  { telemetryName: 'GPU_Reliability_Throttle_Active', label: 'Reliability throttle (active)' },
  { telemetryName: 'GPU_Reliability_Throttle_Count', label: 'Reliability throttle events' },
  { telemetryName: 'GPU_HW_Slowdown_Active', label: 'Hardware slowdown (active)' },
  { telemetryName: 'GPU_HW_Power_Brake_Active', label: 'Hardware power brake (active)' },
];

/**
 * One counter, eight v5 fields. The highest-leverage single line a seller can
 * add to their exporter config: the four fields above plus the thermal and
 * power throttle pairs all decode from this same bitmask.
 */
export const DCGM_EXPORTER_THROTTLE_CONFIG_SNIPPET = `# Add to your dcgm-exporter counters CSV
# (/etc/dcgm-exporter/default-counters.csv, or the file passed via -f):
DCGM_FI_DEV_CLOCK_THROTTLE_REASONS, gauge, Current clock throttle reasons (bitmask)

# It ships commented out, so a stock exporter emits nothing for it.
# Restart the exporter afterwards; the metric appears as dcgm.clock_throttle_reasons.`;

/**
 * v5 fields reachable from `dcgm.*` (dcgm-exporter via the Datadog DCGM
 * check).
 *
 * SIL-144 corrected this list against a real capture: 9 of the 16 names the
 * adapter previously mapped do not exist in Datadog, and ECC never arrives on
 * this path at all — the exporter emits it once the opt-in counters are
 * enabled, but the DCGM check does not forward it (SIL-469).
 *
 * SIL-470 stopped the adapter reading this namespace at all; SIL-479 added
 * it back for the fields below, which is why the adapter reads this list
 * again (apps/telemetry-ingest/src/adapters/datadog.ts's
 * DCGM_ROLLUP_METRIC_NAMES / buildDcgmTelemetryRows).
 */
export const DATADOG_DCGM_COVERED_FIELDS: readonly CoveredField[] = [
  { telemetryName: 'GPU_Utilization_Percent', label: 'GPU utilization' },
  { telemetryName: 'GPU_Temperature_Current_C', label: 'GPU core temperature' },
  { telemetryName: 'GPU_Memory_Temperature_C', label: 'GPU memory temperature' },
  { telemetryName: 'GPU_Power_Draw_Current_W', label: 'Power draw' },
  { telemetryName: 'GPU_SM_Clock_MHz', label: 'SM clock' },
  { telemetryName: 'GPU_Memory_Clock_MHz', label: 'Memory clock' },
  // SIL-508 correction: SIL-144's "does not exist on dcgm.*" claim covered 16
  // previously-mapped names as a group; a live bench re-check (SIL-508,
  // Band B) found DCGM_FI_DEV_PSTATE and DCGM_FI_DEV_POWER_MGMT_LIMIT DO
  // forward through the `dcgm` integration — unlike the ECC/retired/violation
  // families this issue's own §F2 catalogs as dropped. These two are
  // therefore "either namespace", not gpu.*-exclusive; re-bucketed here per
  // the SIL-507 issue's SIL-508-finding follow-up. (Ingesting them into the
  // adapter's dcgm.* query — as opposed to claiming the coverage — is
  // SIL-508's own scope, not this change.)
  { telemetryName: 'GPU_Performance_State', label: 'Performance (P-state)' },
  { telemetryName: 'GPU_Power_Limit_W', label: 'Power limit' },
  ...DATADOG_DCGM_XID_FIELDS,
];

// ---------------------------------------------------------------------------
// SIL-507 — the scoped `siliconx_hw.*` openmetrics check.
//
// A THIRD namespace, distinct from `gpu.*` (native) and `dcgm.*` (the
// Datadog DCGM integration): a generic `openmetrics` check the seller adds
// as a second config object against the same dcgm-exporter endpoint, scoped
// to a NAMED list of families (never a wildcard — see F4 in
// docs/telemetry/datadog-coverage-findings.md for the billing math). The
// namespace itself is a constant WE define (`siliconx_hw`, shipped in the
// conf.yaml handed to the seller) — not seller-chosen and not discovered.
//
// 10 families gate 12 v5 fields (2 of the 10 — the AGG_TOTAL ECC counters —
// also feed a derived growth-rate field, on top of their own raw-value
// field). 8 families/10 fields/885 pts were the original scope; SIL-508's
// live bench measurement found 2 more (ECC_Mode_Current, ECC_Mode_Pending —
// rendered as BARE gauge names, no `.count` suffix, unlike the other 8) for
// +130 pts, bringing the total to 10 families / 12 fields / 1,015 pts.
//
// Structural mirror of apps/telemetry-ingest/src/adapters/datadog.ts's
// SCOPED_COUNTER_FAMILIES / SCOPED_GAUGE_FAMILIES / SCOPED_GROWTH_RATE_FIELDS
// — duplicated here (this module has no dependency on telemetry-ingest, see
// the file header) rather than imported.
// ---------------------------------------------------------------------------

/** The fixed namespace prefix for the scoped check. A constant, not discovered. */
export const SILICONX_HW_NAMESPACE = 'siliconx_hw';

interface ScopedFamilyField {
  /** Full siliconx_hw.* metric name as it lands in Datadog. */
  metricName: string;
  field: CoveredField;
}

/** The 8 counter families — each a direct 1:1 raw-value field, `.count`-suffixed. */
const SCOPED_COUNTER_FIELDS: readonly ScopedFamilyField[] = [
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_ECC_SBE_VOL_TOTAL.count',
    field: { telemetryName: 'ECC_Correctable_Volatile', label: 'ECC correctable errors (volatile)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_ECC_DBE_VOL_TOTAL.count',
    field: { telemetryName: 'ECC_Uncorrectable_Volatile', label: 'ECC uncorrectable errors (volatile)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_ECC_SBE_AGG_TOTAL.count',
    field: { telemetryName: 'ECC_Correctable_Aggregate', label: 'ECC correctable errors (aggregate)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_ECC_DBE_AGG_TOTAL.count',
    field: { telemetryName: 'ECC_Uncorrectable_Aggregate', label: 'ECC uncorrectable errors (aggregate)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_RETIRED_SBE.count',
    field: { telemetryName: 'Retired_Pages_Single_Bit', label: 'Retired pages (single-bit)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_RETIRED_DBE.count',
    field: { telemetryName: 'Retired_Pages_Double_Bit', label: 'Retired pages (double-bit)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_RETIRED_PENDING.count',
    field: { telemetryName: 'Pending_Retired_Pages', label: 'Pending page retirements' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_THERMAL_VIOLATION.count',
    field: { telemetryName: 'GPU_Thermal_Violation_Time_Sec', label: 'Thermal violation time' },
  },
];

/**
 * The 2 gauge families added by SIL-508's finding. Render as BARE metric
 * names — no `.count` suffix — because they are DCGM `gauge` fields, not
 * `counter` fields; do not assume the `.count` rendering the other 8 use.
 */
const SCOPED_GAUGE_FIELDS: readonly ScopedFamilyField[] = [
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_ECC_CURRENT',
    field: { telemetryName: 'ECC_Mode_Current', label: 'ECC mode (current)' },
  },
  {
    metricName: 'siliconx_hw.DCGM_FI_DEV_ECC_PENDING',
    field: { telemetryName: 'ECC_Mode_Pending', label: 'ECC mode (pending reboot)' },
  },
];

/**
 * Growth-rate fields derived from deltas of the two AGG_TOTAL counters
 * (never the VOL_TOTAL ones — those reset on driver reload, AGG persists
 * across it, so it is the safer monotonic source for a rate). Credited only
 * when their SOURCE counter family is resolved-present, since the rate
 * cannot be computed without it.
 */
const SCOPED_GROWTH_RATE_FIELDS: readonly { sourceMetricName: string; field: CoveredField }[] = [
  {
    sourceMetricName: 'siliconx_hw.DCGM_FI_DEV_ECC_DBE_AGG_TOTAL.count',
    field: { telemetryName: 'ECC_Uncorrectable_Growth_Rate', label: 'ECC uncorrectable growth rate' },
  },
  {
    sourceMetricName: 'siliconx_hw.DCGM_FI_DEV_ECC_SBE_AGG_TOTAL.count',
    field: { telemetryName: 'ECC_Correctable_Growth_Rate', label: 'ECC correctable growth rate' },
  },
];

/** All 12 v5 fields the scoped check can unlock, for display purposes (e.g. "0/12 configured"). */
export const DATADOG_SCOPED_ECC_FIELDS: readonly CoveredField[] = [
  ...SCOPED_COUNTER_FIELDS.map((f) => f.field),
  ...SCOPED_GAUGE_FIELDS.map((f) => f.field),
  ...SCOPED_GROWTH_RATE_FIELDS.map((f) => f.field),
];

/** Config handed to the seller: a NAMED 10-family list, never a wildcard (F4 — wildcard exceeds allotment). */
export const SILICONX_HW_OPENMETRICS_CONFIG_SNIPPET = `# /etc/datadog-agent/conf.d/openmetrics.d/conf.yaml
#
# This is the Agent's built-in generic "openmetrics" check config — a
# DIFFERENT check type from the specialised "dcgm" integration your existing
# DCGM check runs (that one lives in conf.d/dcgm.d/conf.yaml and is
# untouched by this). If openmetrics.d/conf.yaml does not exist yet, create
# it with this content. If it already exists (you run other openmetrics
# checks), APPEND this instance to its existing "instances:" list instead of
# overwriting the file.
instances:
  - openmetrics_endpoint: http://localhost:9400/metrics
    namespace: siliconx_hw
    metrics:
      - DCGM_FI_DEV_ECC_SBE_VOL_TOTAL
      - DCGM_FI_DEV_ECC_DBE_VOL_TOTAL
      - DCGM_FI_DEV_ECC_SBE_AGG_TOTAL
      - DCGM_FI_DEV_ECC_DBE_AGG_TOTAL
      - DCGM_FI_DEV_RETIRED_SBE
      - DCGM_FI_DEV_RETIRED_DBE
      - DCGM_FI_DEV_RETIRED_PENDING
      - DCGM_FI_DEV_THERMAL_VIOLATION
      - DCGM_FI_DEV_ECC_CURRENT
      - DCGM_FI_DEV_ECC_PENDING
    rename_labels:
      UUID: gpu_uuid
    exclude_labels:
      - Hostname

# Billing: these arrive as CUSTOM METRICS (billed per timeseries per host),
# unlike the free "dcgm" integration. This named list is exactly 1 series per
# family per GPU — 10 per GPU, 80 on an 8-GPU node — inside the standard
# 100/host Pro allotment at every deployment shape modelled. Matching every
# exporter metric instead of naming these 10 is NOT free — it exceeds the
# allotment on an 8-GPU node — and this is the seller's Datadog bill, not ours.
#
# Fragility: dcgm-exporter's counters CSV is unquoted CSV — one stray comma
# in a description line is FATAL to the exporter (port 9400 goes dark, looks
# like "no GPU metrics" not a config typo). See
# resources/dcgm-exporter-counters/README.md before editing that file; this
# check's config above does not touch it.
#
# Restart: sudo systemctl restart datadog-agent`;

/** One covered v5 field the scoped check unlocks, plus whether that family is confirmed present. */
export interface ScopedEccFamilyStatus extends CoveredField {
  resolved: boolean;
}

export type ScopedEccCheckState = 'not_configured' | 'partial' | 'full';

export interface ScopedEccCoverage {
  state: ScopedEccCheckState;
  /** Per-field status — 12 entries, always in the same order as DATADOG_SCOPED_ECC_FIELDS. */
  fields: ScopedEccFamilyStatus[];
  recommendation: CoverageRecommendation | null;
}

/**
 * Derives the scoped-check sub-report from `datadog_integration.resolved_scoped_families`
 * (a jsonb array of confirmed-present `siliconx_hw.*` metric names, resolved by the
 * adapter's `resolveScopedFamilies()` on a 24h TTL — SIL-507 AC4). This is deliberately
 * a SEPARATE report, not folded into the primary `coveredFields`/`recommendation`, so a
 * seller who reverts the second check is shown as reverted (not silently re-absorbed
 * into "fully covered") — the three-state model the issue's design calls for.
 *
 * `resolvedScopedFamilies: null` (never resolved, or an org with no GPU namespace at
 * all yet) reports `not_configured` with no per-family claim, same shape as a fresh
 * connect being 'pending' rather than 'insufficient'.
 */
export function deriveScopedEccCoverage(
  resolvedScopedFamilies: readonly string[] | null | undefined,
): ScopedEccCoverage {
  const resolvedSet = new Set(resolvedScopedFamilies ?? []);

  const directFields: ScopedEccFamilyStatus[] = [...SCOPED_COUNTER_FIELDS, ...SCOPED_GAUGE_FIELDS].map((f) => ({
    ...f.field,
    resolved: resolvedSet.has(f.metricName),
  }));
  const growthFields: ScopedEccFamilyStatus[] = SCOPED_GROWTH_RATE_FIELDS.map((f) => ({
    ...f.field,
    resolved: resolvedSet.has(f.sourceMetricName),
  }));
  const fields = [...directFields, ...growthFields];

  const resolvedCount = fields.filter((f) => f.resolved).length;
  const state: ScopedEccCheckState =
    resolvedCount === 0 ? 'not_configured' : resolvedCount === fields.length ? 'full' : 'partial';

  const recommendation: CoverageRecommendation | null =
    state === 'full'
      ? null
      : {
          title:
            state === 'not_configured'
              ? 'Add the scoped ECC/retired-page check'
              : 'Finish the scoped ECC/retired-page check',
          body:
            state === 'not_configured'
              ? 'A second, scoped Datadog check unlocks the ECC, retired-page and thermal-violation wear signals your DCGM integration cannot forward (its metric map drops 9 of the 31 families dcgm-exporter emits). This is a NAMED 10-family list, not a wildcard — it stays inside the standard custom-metric allotment at every deployment size we modelled; the wildcard does not.'
              : `${resolvedCount}/${fields.length} of the scoped families are reporting. The rest have not been seen in the last 24h — confirm the check instance in "datadog-agent status" and that the named list above matches exactly (a typo'd metric name resolves to nothing, same as an unconfigured check).`,
          snippet: SILICONX_HW_OPENMETRICS_CONFIG_SNIPPET,
        };

  return { state, fields, recommendation };
}

/**
 * A single actionable recommendation shown on the coverage card.
 *
 * Replaces the previous ECC-counter-specific fields. Those told the seller to
 * add ECC counters to their dcgm-exporter config — a change that cannot close
 * the gap it named, because the Datadog DCGM check drops ECC regardless
 * (SIL-469). Worse, the snippet enabled VOLATILE counters while claiming to
 * fill AGGREGATE fields, which would not have matched even if forwarding
 * worked.
 */
export interface CoverageRecommendation {
  title: string;
  body: string;
  /** Config the seller can paste, or null when the advice needs no config. */
  snippet: string | null;
}

/** Agent config that switches a dcgm-only org onto the richer namespace. */
export const DATADOG_NATIVE_GPU_CONFIG_SNIPPET = `# /etc/datadog-agent/datadog.yaml
gpu:
  enabled: true

# /etc/datadog-agent/system-probe.yaml
gpu_monitoring:
  enabled: true

# Requires Datadog Agent 7.74+ and Linux kernel 5.8+.
# Restart: sudo systemctl restart datadog-agent datadog-agent-sysprobe`;

export type DatadogCoverageState = 'pending' | 'insufficient' | 'covered';

export interface DatadogCoverageReport {
  state: DatadogCoverageState;
  /** Raw prefixes reported present+non-empty by discovery, in discovery order. */
  detectedPrefixes: string[];
  /** The namespace this report's field coverage is drawn from, if any. */
  primaryNamespace: DatadogNamespacePrefix | null;
  coveredFields: CoveredField[];
  /** One actionable next step, or null when there is nothing worth saying. */
  recommendation: CoverageRecommendation | null;
  /** Static, non-scoring-engine copy — SIL-150 owns actual confidence math. */
  confidenceExpectation: string;
}

const PENDING_REPORT: Omit<DatadogCoverageReport, 'confidenceExpectation'> = {
  state: 'pending',
  detectedPrefixes: [],
  primaryNamespace: null,
  coveredFields: [],
  recommendation: null,
};

/**
 * Derives the customer-facing coverage report from a connection's discovery
 * record. Pass `detectedNamespaces: null` and/or `namespacesDiscoveredAt:
 * null` whenever the worker hasn't ticked yet (fresh connect) — this is a
 * normal, expected "pending" state, not an error.
 */
export function deriveDatadogCoverage(
  detectedNamespaces: readonly DatadogNamespaceDiscoveryLike[] | null | undefined,
  namespacesDiscoveredAt: string | Date | null | undefined,
): DatadogCoverageReport {
  if (!namespacesDiscoveredAt || detectedNamespaces == null) {
    return {
      ...PENDING_REPORT,
      confidenceExpectation:
        'Discovery pending — SiliconX checks your Datadog org for GPU telemetry namespaces on the first sync, within the hour.',
    };
  }

  const supported = detectedNamespaces.filter((n) => n.supported && n.metric_count > 0);
  const detectedPrefixes = supported.map((n) => n.prefix);

  const dcgm = supported.find((n) => n.prefix === 'dcgm.');
  const gpu = supported.find((n) => n.prefix === 'gpu.');

  if (!dcgm && !gpu) {
    // Either nothing detected, or only nvml.* (plan decision 8:
    // "nvml.* detected-but-unsupported") — same customer-facing outcome:
    // there is no namespace SiliconX can map into v5 GPU_Health fields.
    //
    // SIL-484: discovery counts metric NAMES present in the org, which cannot
    // separate "no DCGM integration" from "DCGM integration installed,
    // endpoint returning 200, every sample silently dropped". The second case
    // is real — observed on our own hardware: an exporter whose /metrics
    // output omits OpenMetrics `# TYPE` lines makes the Datadog DCGM check
    // report a HEALTHY endpoint while parsing zero samples, so no dcgm.* name
    // ever reaches the org and discovery sees exactly what an uninstalled
    // integration looks like. Telling that customer to install what they
    // already installed sends them in a circle, so the copy names both causes
    // and gives the one check that separates them.
    return {
      state: 'insufficient',
      detectedPrefixes,
      primaryNamespace: null,
      coveredFields: [],
      recommendation: {
        title: 'Enable GPU monitoring in your Datadog Agent',
        body:
          'SiliconX found no GPU metrics it can read. Native GPU Monitoring needs Agent 7.74+ with both settings below; no exporter deployment is required. If you already run the DCGM integration and its check shows green, the problem is upstream of Datadog — see below.',
        snippet: DATADOG_NATIVE_GPU_CONFIG_SNIPPET,
      },
      confidenceExpectation:
        'No usable GPU telemetry reached your Datadog org. Either GPU monitoring is not set up, or it is set up and delivering nothing: an exporter whose /metrics output omits OpenMetrics "# TYPE" lines makes the DCGM check report a healthy endpoint while dropping every sample. If your DCGM check shows green, run "datadog-agent status" on the host and read that check\'s Metric Samples count — 0 confirms this case, and the fix is the exporter, not the integration.',
    };
  }

  // gpu.* is PRIMARY (SIL-152 re-scope). It is the richer namespace and the
  // only one carrying ECC and PCIe degradation — the wear signals that matter
  // most for second-hand valuation. dcgm.* is the fallback, not the ideal.
  //
  // SIL-479: XID (the hardware-fault channel) is credited to dcgm.*
  // SPECIFICALLY, whenever dcgm.* is actually present — including when gpu.*
  // also wins as primary. Silently dropping it because gpu.* is richer would
  // repeat the exact shape of lie SIL-469 fixed for ECC: telling an org it
  // has full coverage when a real signal it lacks (or, here, one it DOES
  // have via a namespace not otherwise credited) is misrepresented either
  // way. dcgm.*-only orgs already get DATADOG_DCGM_XID_FIELDS as part of
  // DATADOG_DCGM_COVERED_FIELDS; this only needs to ALSO surface for the
  // gpu.*-primary case when dcgm.* is present too.
  const primaryNamespace: DatadogNamespacePrefix = gpu ? 'gpu.' : 'dcgm.';
  const coveredFields = gpu
    ? [...DATADOG_GPU_COVERED_FIELDS, ...(dcgm ? DATADOG_DCGM_XID_FIELDS : [])]
    : DATADOG_DCGM_COVERED_FIELDS;

  // Three distinct honest states, not two:
  //  - both present: genuinely fully covered, nothing to recommend.
  //  - gpu.* only: covered for everything gpu.* carries, but the
  //    hardware-fault channel (XID) is a real gap — SIL-479's whole point is
  //    that this must be said explicitly rather than reported as "fully
  //    covered" the way the old (pre-SIL-479) gpu-only branch did.
  //  - dcgm.* only: covered for vitals + XID, but no ECC or PCIe-degradation
  //    (unchanged from SIL-469).
  let recommendation: CoverageRecommendation | null;
  if (gpu && dcgm) {
    // Was `null` — "nothing to recommend". That was true only while the
    // throttle bitmask was miscounted as already-covered. It is opt-in, so an
    // org with both namespaces still has one real gap, and it is the largest
    // one a single config line can close (SIL-484).
    recommendation = {
      title: 'Enable the throttle bitmask in your dcgm-exporter config',
      body:
        "Your org reports both namespaces, so vitals, ECC, PCIe wear and the XID hardware-fault channel are all covered. One gap remains: the clock-throttle bitmask, which ships commented out of dcgm-exporter's default counters. It is a single line, and it is the highest-value one left — eight scoring fields decode from that one metric, covering thermal, power and reliability throttling plus hardware slowdown and power brake. Without it SiliconX cannot tell a GPU that throttles under load from one that never does.",
      snippet: DCGM_EXPORTER_THROTTLE_CONFIG_SNIPPET,
    };
  } else if (gpu) {
    recommendation = {
      title: 'Add dcgm-exporter for hardware-fault detection (XID)',
      body:
        "Your org reports gpu.* only. Native GPU Monitoring covers utilization, memory, ECC and PCIe wear, but XID — NVIDIA's hardware-fault channel (double-bit ECC events, GPU falling off the bus, driver-level failures) — is only reachable through dcgm-exporter and the Datadog DCGM integration. Deploy dcgm-exporter alongside Native GPU Monitoring to add it; the two are complementary, neither replaces the other, and XID is not obtainable by enabling any gpu.* setting.",
      snippet: null,
    };
  } else {
    // SIL-506: the old copy said the DCGM integration "does not forward ECC
    // even when your exporter is configured to emit it", which reads as "ECC
    // cannot reach Datadog from your exporter". Measured 2026-08-06 (bench run
    // 20260806T213258Z): a GENERIC openmetrics check against the same endpoint
    // carries all four ECC totals, the three retired-page counters and thermal
    // violation — the integration's metric map was the constraint, not Datadog.
    //
    // We still recommend Native GPU Monitoring, because it is the only one of
    // the two routes SiliconX can ingest today: the openmetrics band arrives
    // under a seller-chosen namespace the adapter does not yet resolve
    // (SIL-507). So the copy narrows the claim to what is true — the dcgm
    // INTEGRATION drops those families — and no longer implies Native is the
    // only way ECC can travel. Do not add openmetrics-check instructions here
    // until SIL-507 ships; and when it does, the guidance must name the 8
    // scoring families explicitly, never `metrics: ['.*']` (31 series/GPU,
    // over allotment on an 8-GPU node — findings doc §F4).
    recommendation = {
      title: 'Enable native GPU Monitoring for wear signals',
      body:
        'Your org reports dcgm.* only, which covers live vitals and the XID hardware-fault channel, but carries no ECC or PCIe-degradation data — the Datadog DCGM integration maps a fixed field list and drops the ECC and page-retirement counters, even when your exporter emits them. Native GPU Monitoring adds those wear signals, needs no exporter change, and is the route SiliconX reads today. Separately, add DCGM_FI_DEV_CLOCK_THROTTLE_REASONS to your exporter counters: it ships commented out, and eight throttle-related scoring fields decode from that one metric.',
      snippet: DATADOG_NATIVE_GPU_CONFIG_SNIPPET,
    };
  }

  return {
    state: 'covered',
    detectedPrefixes,
    primaryNamespace,
    coveredFields: [...coveredFields],
    recommendation,
    confidenceExpectation: gpu
      ? dcgm
        ? 'These measured gpu.* and dcgm.* fields raise confidence in the GPU Health scoring category, including the ECC/PCIe-degradation wear signals (gpu.*) and the XID hardware-fault channel (dcgm.*) — the combination the confidence model expects.'
        : 'These measured gpu.* fields raise confidence in the GPU Health scoring category, including the ECC and PCIe-degradation wear signals that carry the most weight for resale condition. XID hardware-fault detection is NOT included — it requires dcgm-exporter, which this org does not have connected.'
      : 'dcgm.* provides live utilization, temperature, power and clock vitals plus the XID hardware-fault channel. It cannot provide ECC or PCIe-degradation wear signals, so GPU Health confidence stays below what native GPU Monitoring reaches.',
  };
}
